import React, { useState, useEffect } from 'react';
import { getBackendUrl } from '../utils/apiConfig';
import { Navigate, useLocation } from 'react-router-dom';
import { Loader2, Shield, AlertTriangle } from 'lucide-react';

const ProtectedRoute = ({ children, requireAdmin = false }) => {
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [user, setUser] = useState(null);
  const location = useLocation();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      
      if (!token) {
        setLoading(false);
        return;
      }

      // Validate token with backend
      const response = await fetch(`${getBackendUrl()}/api/auth/me`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
        
        // Check admin requirement
        if (requireAdmin && userData.role !== 'admin') {
          setAuthorized(false);
        } else {
          setAuthorized(true);
        }
      } else {
        // Token invalid, remove from storage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user_data');
      }
    } catch (error) {
      console.error('Auth check failed:', error);
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user_data');
    } finally {
      setLoading(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Vérification des permissions...</p>
        </div>
      </div>
    );
  }

  // Not authenticated
  if (!authorized && !user) {
    // Créer le paramètre de redirection basé sur la route actuelle
    const currentPage = location.pathname.replace('/', '');
    return <Navigate to={`/login?redirect=${currentPage}`} replace />;
  }

  // Authenticated but insufficient permissions
  if (!authorized && user) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="max-w-md mx-auto text-center p-8 bg-white rounded-lg shadow-lg">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Accès refusé</h2>
          <p className="text-gray-600 mb-6">
            Vous n'avez pas les permissions nécessaires pour accéder à cette page.
          </p>
          <div className="space-y-3">
            <div className="bg-gray-50 p-4 rounded-lg">
              <p className="text-sm text-gray-600">Connecté en tant que:</p>
              <p className="font-medium">{user.name}</p>
              <p className="text-sm text-gray-500">Role: {user.role}</p>
            </div>
            <a 
              href="/login" 
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
            >
              Se connecter avec un compte admin
            </a>
            <a 
              href="/" 
              className="block text-gray-600 hover:text-gray-800"
            >
              Retour au site
            </a>
          </div>
        </div>
      </div>
    );
  }

  // Authorized
  return children;
};

export default ProtectedRoute;